import { Vector2 } from '@shared/types';
import { GAME_CONFIG } from '@shared/constants';
import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { MapGenerator } from './MapGenerator';
import { getZoneTemplate, PropPlacement, TileType, BiomeType, SpawnPoint } from './MapTemplates';
import { PropType } from '@client/assets/props';
import { PropRegistry } from '@client/assets/props';
import { ZoneType } from '@shared/world/Zone';
import { renderTerrainBuffer, /* renderTileDetailOverlay, */ clearProceduralCaches } from './ProceduralTileRenderer';
import { getTileImage } from './TileSprites';
import { debugLog } from '@shared/utils/Logger';

export interface Tile {
  type: TileType;
  walkable: boolean;
  elevation: number;
  variant: number; // For visual variety
}

// Re-export for other modules
export { TileType };
export type { BiomeType };

export const TILE_COLORS: Record<TileType, { base: string; light: string; dark: string }> = {
  [TileType.GRASS]:         { base: '#4d7a3e', light: '#6ba354', dark: '#2e5425' },
  [TileType.DIRT]:          { base: '#7d6548', light: '#9a7f5c', dark: '#503a24' },
  [TileType.STONE]:         { base: '#6a6a6e', light: '#8e8e94', dark: '#3e3e42' },
  [TileType.WATER]:         { base: '#2c5f82', light: '#3d7faa', dark: '#1a3d5e' },
  [TileType.SAND]:          { base: '#c4ad82', light: '#ddc89c', dark: '#8e7a52' },
  [TileType.DARK_GRASS]:    { base: '#2c4e26', light: '#3e6e38', dark: '#152812' },
  [TileType.MUD]:           { base: '#4d3926', light: '#6a5038', dark: '#2e2014' },
  [TileType.SNOW]:          { base: '#dde0ee', light: '#f5f5ff', dark: '#b0b4c8' },
  [TileType.LAVA]:          { base: '#e83800', light: '#ff6622', dark: '#a02000' },
  [TileType.VOID]:          { base: '#14142a', light: '#22223a', dark: '#060612' },
  [TileType.HILL]:          { base: '#5a8646', light: '#72a85c', dark: '#3a5c2a' },
  [TileType.MOUNTAIN]:      { base: '#585860', light: '#787880', dark: '#303038' },
  [TileType.SHALLOW_WATER]: { base: '#3e7e96', light: '#56a0bc', dark: '#265a70' },
  [TileType.DEEP_WATER]:    { base: '#1e3e5e', light: '#2e5e82', dark: '#0e2240' },
  [TileType.SWAMP_WATER]:   { base: '#2e4e2e', light: '#3e6e3e', dark: '#1a361a' },
  [TileType.CLIFF_EDGE]:    { base: '#4a4838', light: '#6a6858', dark: '#24221a' },
  [TileType.ROCKY_GROUND]:  { base: '#52524e', light: '#727270', dark: '#2e2e2c' },
};

// Bridge tile cache
const bridgeTileCache: Map<string, HTMLCanvasElement> = new Map();

function getOrCreateBridgeTile(zoom: number, tileWidth: number, tileHeight: number): HTMLCanvasElement {
  const key = `bridge_${zoom.toFixed(2)}`;

  if (bridgeTileCache.has(key)) {
    return bridgeTileCache.get(key)!;
  }

  const w = tileWidth * zoom;
  const h = tileHeight * zoom;
  const halfW = w / 2;
  const halfH = h / 2;

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(w) + 2;
  canvas.height = Math.ceil(h) + 2;
  const ctx = canvas.getContext('2d')!;

  const cx = canvas.width / 2;
  const cy = canvas.height / 2;

  // Draw wooden bridge planks
  ctx.beginPath();
  ctx.moveTo(cx, cy - halfH);
  ctx.lineTo(cx + halfW, cy);
  ctx.lineTo(cx, cy + halfH);
  ctx.lineTo(cx - halfW, cy);
  ctx.closePath();

  // Wood color gradient
  const gradient = ctx.createLinearGradient(cx - halfW, cy - halfH, cx + halfW, cy + halfH);
  gradient.addColorStop(0, '#9b8365');
  gradient.addColorStop(0.5, '#7b6345');
  gradient.addColorStop(1, '#5b4325');
  ctx.fillStyle = gradient;
  ctx.fill();

  // Outline
  ctx.strokeStyle = '#4a3628';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Plank lines
  ctx.strokeStyle = '#4a362880';
  ctx.lineWidth = 1;
  for (let i = -2; i <= 2; i++) {
    ctx.beginPath();
    ctx.moveTo(cx - halfW * 0.8 + i * halfW * 0.15, cy - halfH * 0.3 + i * halfH * 0.15);
    ctx.lineTo(cx + halfW * 0.3 + i * halfW * 0.15, cy + halfH * 0.3 + i * halfH * 0.15);
    ctx.stroke();
  }

  bridgeTileCache.set(key, canvas);
  return canvas;
}

// Clear tile cache
export function clearTileCache(): void {
  bridgeTileCache.clear();
  clearProceduralCaches();
  lastCacheZoom = -1;
}

// Clear cache when zoom changes significantly
let lastCacheZoom = -1;
function clearBridgeCacheIfNeeded(zoom: number): void {
  const roundedZoom = Math.round(zoom * 10) / 10;
  if (lastCacheZoom !== roundedZoom) {
    bridgeTileCache.clear();
    clearProceduralCaches();
    lastCacheZoom = roundedZoom;
  }
}

export class IsometricMap {
  private width: number;
  private height: number;
  private tiles: Tile[][];
  private tileWidth: number;
  private tileHeight: number;
  private biome: BiomeType;
  private seed: number;
  private props: PropPlacement[] = [];
  private sortedPropsCache: PropPlacement[] = [];
  private propsDirty: boolean = true;
  private spawns: SpawnPoint[] = [];
  private bridgeTiles: Set<number> = new Set();
  private zoneId: ZoneType | null = null;
  private frameTime: number = 0;
  private heightmap: number[][] = [];

  // Spatial hash grid for fast prop collision queries
  private propGrid: Map<number, PropPlacement[]> = new Map();
  private propGridCellSize: number = 3; // tiles per cell

  // Terrain buffer (continuous ground rendering — no tile seams)
  private terrainCanvas: HTMLCanvasElement | null = null;
  private terrainCtx: CanvasRenderingContext2D | null = null;
  private terrainImageData: ImageData | null = null;
  private terrainOriginWX: number = 0;
  private terrainOriginWY: number = 0;
  private terrainZoom: number = -1;

  // Tile sprite buffer (caches all tile sprites + bridges to offscreen canvas)
  // Re-rendered only when camera moves beyond threshold — reduces ~800 drawImage/frame to 1
  private tileSpriteBuf: HTMLCanvasElement | null = null;
  private tileSpriteCtx: CanvasRenderingContext2D | null = null;
  private tsBufW: number = 0;
  private tsBufH: number = 0;
  private tsCamWX: number = 0; // camera world X when buffer was rendered
  private tsCamWY: number = 0;
  private tsZoom: number = -1;
  private readonly tsPad: number = 250; // extra pixels per side


  // Pre-allocated transparent prop buffer (avoids per-frame allocation)
  private _tpBuf: { type: PropType; sx: number; sy: number; variant?: number }[] = [];

  constructor(width: number, height: number, biome: BiomeType = 'forest', seed?: number) {
    this.width = width;
    this.height = height;
    this.tileWidth = GAME_CONFIG.TILE_WIDTH;
    this.tileHeight = GAME_CONFIG.TILE_HEIGHT;
    this.biome = biome;
    this.seed = seed ?? Math.floor(Math.random() * 1000000);
    this.tiles = [];

    // Don't generate here - wait for setZone to be called
  }

  /**
   * Clear all cached rendering data for this map instance (terrain buffer, vignette,
   * sprite batch buffers). Called before a zone transition to free GPU-backed canvases.
   */
  clearZoneCache(): void {
    this.terrainCanvas = null;
    this.terrainCtx = null;
    this.terrainImageData = null;
    this.terrainZoom = -1;
    this.tileSpriteBuf = null;
    this.tileSpriteCtx = null;
    this.tsZoom = -1;
    this.sortedPropsCache = [];
    this.propsDirty = true;
  }

  // Set zone and use its template if available
  setZone(zoneId: ZoneType, biome: BiomeType): void {
    this.zoneId = zoneId;
    this.biome = biome;
    this.generateMap();
    debugLog('IsometricMap', `Zone set: ${zoneId}, dimensions: ${this.width}x${this.height}`);
  }

  setBiome(biome: BiomeType): void {
    this.biome = biome;
    this.seed = Math.floor(Math.random() * 1000000);
    this.generateMap();
  }

  private generateMap(): void {
    // Try to use a predefined template first
    const template = this.zoneId ? getZoneTemplate(this.zoneId) : undefined;

    if (template) {
      this.generateFromTemplate(template);
    } else {
      this.generateRandom();
    }
  }

  private generateFromTemplate(template: import('./MapTemplates').MapTemplate): void {
    this.biome = template.biome;
    this.tiles = [];
    this.props = [];
    this.propsDirty = true;
    this.bridgeTiles.clear();

    // Use map dimensions from template
    this.width = template.width;
    this.height = template.height;

    // Copy tiles directly from template
    for (let y = 0; y < this.height; y++) {
      this.tiles[y] = [];
      for (let x = 0; x < this.width; x++) {
        const type = template.tiles[y]?.[x] ?? TileType.GRASS;
        this.tiles[y][x] = {
          type,
          walkable: type !== TileType.WATER && type !== TileType.LAVA && type !== TileType.VOID && type !== TileType.MOUNTAIN && type !== TileType.DEEP_WATER && type !== TileType.CLIFF_EDGE,
          elevation: 0,
          variant: Math.floor(Math.random() * 4),
        };
      }
    }

    // Build synthetic heightmap from tile types and smooth (3 passes for nice gradients)
    this.buildHeightmapFromTiles();
    this.smoothHeightmap(3);

    // Copy props directly from template
    this.props = [...template.props];
    this.propsDirty = true;

    // Copy spawns from template
    this.spawns = template.spawns ? [...template.spawns] : [];

    // Build spatial hash for fast prop collision
    this.buildPropGrid();
  }

  // Get player spawn position (tile coordinates)
  getPlayerSpawn(): Vector2 | null {
    const playerSpawn = this.spawns.find(s => s.type === 'player');
    if (playerSpawn) {
      // Convert tile coordinates to world coordinates
      return this.tileToWorld(playerSpawn.x, playerSpawn.y);
    }
    return null;
  }

  // Get all enemy spawn positions (world coordinates)
  getEnemySpawns(): Vector2[] {
    return this.spawns
      .filter(s => s.type === 'enemy')
      .map(s => this.tileToWorld(s.x, s.y));
  }

  // Get boss spawn position
  getBossSpawn(): Vector2 | null {
    const bossSpawn = this.spawns.find(s => s.type === 'boss');
    if (bossSpawn) {
      return this.tileToWorld(bossSpawn.x, bossSpawn.y);
    }
    return null;
  }

  // Get portal spawn positions
  getPortalSpawns(): Vector2[] {
    return this.spawns
      .filter(s => s.type === 'portal')
      .map(s => this.tileToWorld(s.x, s.y));
  }

  private tileTypeToElevation(type: TileType): number {
    switch (type) {
      case TileType.DEEP_WATER:    return 0.10;
      case TileType.WATER:         return 0.20;
      case TileType.SHALLOW_WATER: return 0.30;
      case TileType.SWAMP_WATER:   return 0.25;
      case TileType.MUD:           return 0.35;
      case TileType.SAND:          return 0.38;
      case TileType.GRASS:         return 0.45;
      case TileType.DARK_GRASS:    return 0.45;
      case TileType.DIRT:          return 0.42;
      case TileType.STONE:         return 0.50;
      case TileType.ROCKY_GROUND:  return 0.65;
      case TileType.CLIFF_EDGE:    return 0.70;
      case TileType.HILL:          return 0.75;
      case TileType.MOUNTAIN:      return 0.90;
      case TileType.SNOW:          return 0.80;
      case TileType.LAVA:          return 0.30;
      case TileType.VOID:          return 0.10;
      default:                     return 0.45;
    }
  }

  private buildHeightmapFromTiles(): void {
    this.heightmap = [];
    for (let y = 0; y < this.height; y++) {
      this.heightmap[y] = [];
      for (let x = 0; x < this.width; x++) {
        const baseElev = this.tileTypeToElevation(this.tiles[y][x].type);
        // Add deterministic micro-noise so flat areas aren't perfectly uniform
        let h = (this.seed * 374761393 + x * 668265263 + y * 1274126177) | 0;
        h = ((h ^ (h >> 13)) * 1103515245) | 0;
        h = (h ^ (h >> 16)) | 0;
        const microNoise = ((h & 0x7fffffff) / 0x7fffffff - 0.5) * 0.06;
        this.heightmap[y][x] = Math.max(0, Math.min(1, baseElev + microNoise));
      }
    }
  }

  private smoothHeightmap(passes: number): void {
    for (let pass = 0; pass < passes; pass++) {
      const copy: number[][] = [];
      for (let y = 0; y < this.height; y++) {
        copy[y] = [];
        for (let x = 0; x < this.width; x++) {
          let sum = 0, count = 0;
          // Wider kernel on later passes for broader smoothing
          const radius = pass < 2 ? 1 : 2;
          for (let dy = -radius; dy <= radius; dy++) {
            for (let dx = -radius; dx <= radius; dx++) {
              const ny = y + dy, nx = x + dx;
              if (ny >= 0 && ny < this.height && nx >= 0 && nx < this.width) {
                // Distance-based weight: center heavier
                const dist = Math.abs(dx) + Math.abs(dy);
                const w = 1 / (1 + dist * 0.5);
                sum += this.heightmap[ny][nx] * w;
                count += w;
              }
            }
          }
          copy[y][x] = sum / count;
        }
      }
      this.heightmap = copy;
    }
  }

  /** Build spatial hash grid for prop collision — O(1) average lookup instead of O(n) */
  private buildPropGrid(): void {
    this.propGrid.clear();
    const cs = this.propGridCellSize;
    for (const prop of this.props) {
      const asset = PropRegistry.get(prop.type);
      if (!asset || asset.walkable) continue;
      // Insert into the cell the prop center is in, plus neighbors within collision radius
      const r = Math.ceil(asset.collisionRadius / cs);
      const cx0 = Math.floor(prop.x / cs);
      const cy0 = Math.floor(prop.y / cs);
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          const key = (cx0 + dx) * 73856093 + (cy0 + dy) * 19349669;
          let cell = this.propGrid.get(key);
          if (!cell) { cell = []; this.propGrid.set(key, cell); }
          cell.push(prop);
        }
      }
    }
  }

  private generateRandom(): void {
    // Fallback to random generation for zones without templates
    const generator = new MapGenerator(this.width, this.height, this.biome, this.seed);
    const result = generator.generate();

    this.tiles = [];
    for (let y = 0; y < this.height; y++) {
      this.tiles[y] = [];
      for (let x = 0; x < this.width; x++) {
        const type = result.tiles[y][x];
        this.tiles[y][x] = {
          type,
          walkable: type !== TileType.WATER && type !== TileType.LAVA && type !== TileType.VOID && type !== TileType.MOUNTAIN && type !== TileType.DEEP_WATER && type !== TileType.CLIFF_EDGE,
          elevation: 0,
          variant: Math.floor(Math.random() * 4),
        };
      }
    }

    this.heightmap = result.heightmap;

    // Convert MapFeatures to PropPlacements
    this.props = result.features.map(f => ({
      type: f.type as PropType,
      x: f.x,
      y: f.y,
      variant: f.variant
    }));
    this.propsDirty = true;
    this.bridgeTiles = result.bridgeTiles;

    // Build spatial hash for fast prop collision
    this.buildPropGrid();
  }

  // Convert tile coordinates to isometric world coordinates
  tileToWorld(tileX: number, tileY: number): Vector2 {
    return {
      x: (tileX - tileY) * (this.tileWidth / 2),
      y: (tileX + tileY) * (this.tileHeight / 2),
    };
  }

  // Convert world coordinates to tile coordinates
  worldToTile(worldX: number, worldY: number): Vector2 {
    const tileX = (worldX / (this.tileWidth / 2) + worldY / (this.tileHeight / 2)) / 2;
    const tileY = (worldY / (this.tileHeight / 2) - worldX / (this.tileWidth / 2)) / 2;

    return {
      x: Math.floor(tileX),
      y: Math.floor(tileY),
    };
  }

  // Check if tile coordinates are valid
  isValidTile(tileX: number, tileY: number): boolean {
    return tileX >= 0 && tileX < this.width && tileY >= 0 && tileY < this.height;
  }

  // Check if a tile is walkable
  isWalkable(tileX: number, tileY: number): boolean {
    if (!this.isValidTile(tileX, tileY)) return false;
    return this.tiles[tileY][tileX].walkable;
  }

  // Check if world position is walkable (including prop collision)
  isWorldPositionWalkable(worldX: number, worldY: number): boolean {
    const tile = this.worldToTile(worldX, worldY);

    // First check tile walkability
    if (!this.isWalkable(tile.x, tile.y)) {
      return false;
    }

    // Then check prop collisions
    return !this.collidesWithProp(tile.x, tile.y);
  }

  // Get movement speed multiplier for the tile at world position
  // Returns 1.0 for normal, <1.0 for slowing tiles
  getMovementSpeedMultiplier(worldX: number, worldY: number): number {
    const tilePos = this.worldToTile(worldX, worldY);
    const tile = this.getTile(tilePos.x, tilePos.y);
    if (!tile) return 1.0;

    switch (tile.type) {
      case TileType.SHALLOW_WATER: return 0.7;  // 30% slower
      case TileType.SWAMP_WATER: return 0.5;    // 50% slower
      case TileType.MUD: return 0.8;            // 20% slower
      case TileType.HILL: return 0.85;          // 15% slower (uphill)
      default: return 1.0;
    }
  }

  // Get tile type at world position
  getTileTypeAtWorld(worldX: number, worldY: number): TileType | null {
    const tilePos = this.worldToTile(worldX, worldY);
    const tile = this.getTile(tilePos.x, tilePos.y);
    return tile ? tile.type : null;
  }

  // Check if a tile position collides with any prop (spatial hash — O(1) average)
  collidesWithProp(tileX: number, tileY: number): boolean {
    const cs = this.propGridCellSize;
    const key = Math.floor(tileX / cs) * 73856093 + Math.floor(tileY / cs) * 19349669;
    const cell = this.propGrid.get(key);
    if (!cell) return false;
    for (const prop of cell) {
      const asset = PropRegistry.get(prop.type);
      if (!asset || asset.walkable) continue;
      const dx = tileX - prop.x;
      const dy = tileY - prop.y;
      if (dx * dx + dy * dy < asset.collisionRadius * asset.collisionRadius) return true;
    }
    return false;
  }

  // Check collision with props using world coordinates (spatial hash — O(1) average)
  collidesWithPropWorld(worldX: number, worldY: number, entityRadius: number = 0.3): boolean {
    const entityTile = this.worldToTile(worldX, worldY);
    const cs = this.propGridCellSize;
    const key = Math.floor(entityTile.x / cs) * 73856093 + Math.floor(entityTile.y / cs) * 19349669;
    const cell = this.propGrid.get(key);
    if (!cell) return false;
    for (const prop of cell) {
      const asset = PropRegistry.get(prop.type);
      if (!asset || asset.walkable) continue;
      const dx = entityTile.x - prop.x;
      const dy = entityTile.y - prop.y;
      const r = asset.collisionRadius + entityRadius;
      if (dx * dx + dy * dy < r * r) return true;
    }
    return false;
  }

  // Get tile at position
  getTile(tileX: number, tileY: number): Tile | null {
    if (!this.isValidTile(tileX, tileY)) return null;
    return this.tiles[tileY][tileX];
  }

  update(_deltaTime: number): void {
    // Features are static, no update needed
  }

  // Pre-allocated buffers for water shimmer screen positions
  private _waterSX: Float64Array = new Float64Array(4096);
  private _waterSY: Float64Array = new Float64Array(4096);
  private _waterPhase: Float64Array = new Float64Array(4096);

  render(renderer: Renderer, camera: Camera): void {
    this.frameTime = Date.now();
    const ctx = renderer.context;
    const zoom = camera.zoom;
    const screenW = renderer.width;
    const screenH = renderer.height;

    // Clear bridge cache if zoom changed
    clearBridgeCacheIfNeeded(zoom);

    // 1. Render continuous terrain buffer (seamless ground surface + elevation shading)
    this.renderTerrainPass(ctx, camera, screenW, screenH, zoom);

    // 2. Render tile sprites + bridges (cached to offscreen canvas — ~800 drawImage → 1 blit)
    this.renderTileSpritePass(ctx, camera, screenW, screenH, zoom);

    // 3. Water shimmer (animated per frame, lightweight)
    this.renderWaterShimmer(ctx, camera, screenW, screenH, zoom);
  }

  /** Render tile sprites + bridges to a cached offscreen canvas.
   *  Re-rendered only when camera moves beyond padding threshold. */
  private renderTileSpritePass(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    screenW: number,
    screenH: number,
    zoom: number,
  ): void {
    const pad = this.tsPad;
    const bufW = screenW + pad * 2;
    const bufH = screenH + pad * 2;
    const camX = camera.position.x;
    const camY = camera.position.y;

    const needsRender = !this.tileSpriteBuf ||
      this.tsBufW !== bufW || this.tsBufH !== bufH ||
      this.tsZoom !== zoom ||
      Math.abs(camX - this.tsCamWX) * zoom > pad * 0.5 ||
      Math.abs(camY - this.tsCamWY) * zoom > pad * 0.5;

    if (needsRender) {
      if (!this.tileSpriteBuf || this.tsBufW !== bufW || this.tsBufH !== bufH) {
        this.tileSpriteBuf = document.createElement('canvas');
        this.tileSpriteBuf.width = bufW;
        this.tileSpriteBuf.height = bufH;
        this.tileSpriteCtx = this.tileSpriteBuf.getContext('2d')!;
        this.tsBufW = bufW;
        this.tsBufH = bufH;
      }

      const bCtx = this.tileSpriteCtx!;
      bCtx.clearRect(0, 0, bufW, bufH);

      // Compute visible tile range for the padded buffer area
      const invZoom = 1 / zoom;
      const halfTW = this.tileWidth / 2;
      const halfTH = this.tileHeight / 2;
      const invHalfTW = 1 / halfTW;
      const invHalfTH = 1 / halfTH;

      // Screen corners expanded by pad → world → tile
      const corners = [
        [-pad, -pad], [screenW + pad, -pad],
        [-pad, screenH + pad], [screenW + pad, screenH + pad],
      ];
      let tMinX = Infinity, tMaxX = -Infinity, tMinY = Infinity, tMaxY = -Infinity;
      for (const [sx, sy] of corners) {
        const wx = sx * invZoom + camX;
        const wy = sy * invZoom + camY;
        const tx = Math.floor((wx * invHalfTW + wy * invHalfTH) / 2);
        const ty = Math.floor((wy * invHalfTH - wx * invHalfTW) / 2);
        if (tx < tMinX) tMinX = tx;
        if (tx > tMaxX) tMaxX = tx;
        if (ty < tMinY) tMinY = ty;
        if (ty > tMaxY) tMaxY = ty;
      }

      const tilePad = 3;
      const minTileX = Math.max(0, tMinX - tilePad);
      const maxTileX = Math.min(this.width - 1, tMaxX + tilePad);
      const minTileY = Math.max(0, tMinY - tilePad);
      const maxTileY = Math.min(this.height - 1, tMaxY + tilePad);

      const spriteDrawW = this.tileWidth * zoom;
      const spriteDrawH = this.tileHeight * zoom;

      // Buffer coords: (0,0) corresponds to screen(-pad, -pad) with current camera
      // bufX = (wx - camX) * zoom + pad
      for (let y = minTileY; y <= maxTileY; y++) {
        for (let x = minTileX; x <= maxTileX; x++) {
          if (y < 0 || y >= this.height || x < 0 || x >= this.width) continue;

          const tile = this.tiles[y][x];
          const wx = (x - y) * halfTW;
          const wy = (x + y) * halfTH;
          const bufX = (wx - camX) * zoom + pad;
          const bufY = (wy - camY) * zoom + pad;

          if (this.bridgeTiles.has(x * 10000 + y)) {
            this.renderBridgeTile(bCtx, bufX, bufY, zoom);
            continue;
          }

          const spriteImg = getTileImage(tile.type, tile.variant);
          if (spriteImg) {
            bCtx.drawImage(spriteImg, bufX - spriteDrawW / 2, bufY - spriteDrawH / 2, spriteDrawW, spriteDrawH);
          }
        }
      }

      this.tsCamWX = camX;
      this.tsCamWY = camY;
      this.tsZoom = zoom;
    }

    // Blit cached tile sprite buffer to main canvas.
    // Buffer was rendered with camera at (tsCamWX, tsCamWY). Current camera may have moved.
    const offsetX = (this.tsCamWX - camera.position.x) * zoom + camera.shakeOffsetX;
    const offsetY = (this.tsCamWY - camera.position.y) * zoom + camera.shakeOffsetY;
    ctx.drawImage(this.tileSpriteBuf!, -this.tsPad + offsetX, -this.tsPad + offsetY);
  }

  /** Render animated water shimmer (per-frame, lightweight path-based drawing) */
  private renderWaterShimmer(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    screenW: number,
    screenH: number,
    zoom: number,
  ): void {
    const camX = camera.position.x;
    const camY = camera.position.y;
    const shakeX = camera.shakeOffsetX;
    const shakeY = camera.shakeOffsetY;
    const invZoom = 1 / zoom;
    const halfTW = this.tileWidth / 2;
    const halfTH = this.tileHeight / 2;
    const invHalfTW = 1 / halfTW;
    const invHalfTH = 1 / halfTH;

    // Get visible tile range
    const corners = [
      [0, 0], [screenW, 0], [0, screenH], [screenW, screenH]
    ];
    let tMinX = Infinity, tMaxX = -Infinity, tMinY = Infinity, tMaxY = -Infinity;
    for (const [sx, sy] of corners) {
      const wx = sx * invZoom + camX;
      const wy = sy * invZoom + camY;
      const tx = Math.floor((wx * invHalfTW + wy * invHalfTH) / 2);
      const ty = Math.floor((wy * invHalfTH - wx * invHalfTW) / 2);
      if (tx < tMinX) tMinX = tx;
      if (tx > tMaxX) tMaxX = tx;
      if (ty < tMinY) tMinY = ty;
      if (ty > tMaxY) tMaxY = ty;
    }

    const tilePad = 2;
    const minTileX = Math.max(0, tMinX - tilePad);
    const maxTileX = Math.min(this.width - 1, tMaxX + tilePad);
    const minTileY = Math.max(0, tMinY - tilePad);
    const maxTileY = Math.min(this.height - 1, tMaxY + tilePad);

    // Collect visible water tiles
    let waterCount = 0;
    const margin = this.tileWidth * zoom;
    for (let y = minTileY; y <= maxTileY; y++) {
      for (let x = minTileX; x <= maxTileX; x++) {
        if (y < 0 || y >= this.height || x < 0 || x >= this.width) continue;
        const tt = this.tiles[y][x].type;
        if (tt !== TileType.WATER && tt !== TileType.SHALLOW_WATER &&
            tt !== TileType.DEEP_WATER && tt !== TileType.SWAMP_WATER) continue;

        const wx = (x - y) * halfTW;
        const wy = (x + y) * halfTH;
        const scrX = (wx - camX) * zoom + shakeX;
        const scrY = (wy - camY) * zoom + shakeY;
        if (scrX < -margin || scrX > screenW + margin ||
            scrY < -margin || scrY > screenH + margin) continue;

        this._waterSX[waterCount] = scrX;
        this._waterSY[waterCount] = scrY;
        this._waterPhase[waterCount] = x * 0.5 + y * 0.3 + tt * 100;
        waterCount++;
      }
    }

    if (waterCount === 0) return;

    const halfW = this.tileWidth * zoom / 2;
    const halfH = this.tileHeight * zoom / 2;
    const time = this.frameTime / 1000;

    ctx.save();
    ctx.lineWidth = 1.5 * zoom;

    const waterTypes: Array<[number, string, number]> = [
      [TileType.WATER, 'rgb(150, 200, 255)', 0.05],
      [TileType.SHALLOW_WATER, 'rgb(180, 220, 255)', 0.06],
      [TileType.DEEP_WATER, 'rgb(100, 150, 220)', 0.04],
      [TileType.SWAMP_WATER, 'rgb(80, 140, 80)', 0.05],
    ];

    for (const [typeVal, color, baseAlpha] of waterTypes) {
      ctx.strokeStyle = color;
      ctx.globalAlpha = baseAlpha;
      ctx.beginPath();

      let hasLines = false;
      const typeOffset = typeVal * 100;
      for (let i = 0; i < waterCount; i += 4) {
        const rawPhase = this._waterPhase[i];
        const encodedType = Math.round((rawPhase % 1000) - (rawPhase % 100));
        if (Math.abs(encodedType - typeOffset) > 50) continue;

        const sx = this._waterSX[i];
        const sy = this._waterSY[i];
        const phase = rawPhase - typeOffset;
        const waveOffset = Math.sin(time * 2 + phase) * halfH * 0.2;

        ctx.moveTo(sx - halfW * 0.6, sy + waveOffset);
        ctx.quadraticCurveTo(sx, sy - halfH * 0.15 + waveOffset, sx + halfW * 0.6, sy + waveOffset);
        hasLines = true;
      }

      if (hasLines) ctx.stroke();
    }

    ctx.restore();
  }

  private renderTerrainPass(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    screenW: number,
    screenH: number,
    zoom: number,
  ): void {
    const scale = 6;       // 1/6 resolution for performance (was 4 → ~60% fewer pixels)
    const pad = 450;       // extra pixels per side (larger buffer = less frequent re-renders)
    const fullW = screenW + pad * 2;
    const fullH = screenH + pad * 2;
    const bufW = Math.ceil(fullW / scale);
    const bufH = Math.ceil(fullH / scale);

    const invZoom = 1 / zoom;

    // World-space origin of the buffer (screen position -pad, -pad)
    const curOriginWX = camera.position.x - pad * invZoom;
    const curOriginWY = camera.position.y - pad * invZoom;

    // Re-render when camera has moved beyond half the padding, or zoom changed, or buffer resized
    const needsRender = !this.terrainCanvas ||
      this.terrainCanvas.width !== bufW ||
      this.terrainCanvas.height !== bufH ||
      this.terrainZoom !== zoom ||
      Math.abs(curOriginWX - this.terrainOriginWX) * zoom > pad * 0.55 ||
      Math.abs(curOriginWY - this.terrainOriginWY) * zoom > pad * 0.55;

    if (needsRender) {
      if (!this.terrainCanvas || this.terrainCanvas.width !== bufW || this.terrainCanvas.height !== bufH) {
        this.terrainCanvas = document.createElement('canvas');
        this.terrainCanvas.width = bufW;
        this.terrainCanvas.height = bufH;
        this.terrainCtx = this.terrainCanvas.getContext('2d')!;
        this.terrainImageData = this.terrainCtx.createImageData(bufW, bufH);
      }

      const step = scale * invZoom;
      const imageData = this.terrainImageData!;
      const getElevation = (tx: number, ty: number): number => {
        if (tx < 0 || tx >= this.width || ty < 0 || ty >= this.height) {
          const cx = Math.max(0, Math.min(this.width - 1, tx));
          const cy = Math.max(0, Math.min(this.height - 1, ty));
          return this.heightmap[cy]?.[cx] ?? 0.45;
        }
        return this.heightmap[ty]?.[tx] ?? 0.45;
      };
      renderTerrainBuffer(
        imageData.data, bufW, bufH,
        curOriginWX, curOriginWY, step,
        (tx: number, ty: number) => {
          const t = this.getTile(tx, ty);
          return t ? t.type : null;
        },
        this.tileWidth, this.tileHeight,
        getElevation,
      );
      this.terrainCtx!.putImageData(imageData, 0, 0);

      this.terrainOriginWX = curOriginWX;
      this.terrainOriginWY = curOriginWY;
      this.terrainZoom = zoom;
    }

    // Draw terrain buffer at correct screen position (accounts for camera movement + shake)
    // Nearest-neighbor upscale — terrain is noisy so bilinear adds no visible quality
    const drawPos = camera.worldToScreen(this.terrainOriginWX, this.terrainOriginWY);
    const prevSmoothing = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false;

    // Clip source rect to avoid GPU work on off-screen pixels
    const dx = drawPos.x;
    const dy = drawPos.y;
    const dw = fullW;
    const dh = fullH;

    // Calculate visible portion only (skip pixels that would be clipped by canvas)
    const clipL = Math.max(0, -dx);        // left pixels off-screen in dest space
    const clipT = Math.max(0, -dy);        // top pixels off-screen
    const clipR = Math.max(0, (dx + dw) - screenW); // right pixels off-screen
    const clipB = Math.max(0, (dy + dh) - screenH); // bottom pixels off-screen

    const srcScale = bufW / dw;  // dest→src scaling factor
    const sx = clipL * srcScale;
    const sy = clipT * srcScale;
    const sw = bufW - (clipL + clipR) * srcScale;
    const sh = bufH - (clipT + clipB) * srcScale;

    if (sw > 0 && sh > 0) {
      ctx.drawImage(
        this.terrainCanvas!,
        sx, sy, sw, sh,
        dx + clipL, dy + clipT, dw - clipL - clipR, dh - clipT - clipB,
      );
    }
    ctx.imageSmoothingEnabled = prevSmoothing;
  }

  renderProps(renderer: Renderer, camera: Camera, playerWorldPos?: Vector2): void {
    const ctx = renderer.context;
    const zoom = camera.zoom;
    const screenW = renderer.width;
    const screenH = renderer.height;

    // Update PropRegistry scale for caching
    PropRegistry.setScale(zoom);

    // Convert player world position to tile coords for comparison
    let playerTilePos: Vector2 | null = null;
    if (playerWorldPos) {
      playerTilePos = this.worldToTile(playerWorldPos.x, playerWorldPos.y);
    }

    // Sort props by Y position for proper depth ordering (cached)
    if (this.propsDirty) {
      this.sortedPropsCache = [...this.props].sort((a, b) => a.y - b.y);
      this.propsDirty = false;
    }

    // Pre-compute camera transform constants to inline tileToWorld + worldToScreen
    // tileToWorld: worldX = (x - y) * (tileWidth / 2), worldY = (x + y) * (tileHeight / 2)
    // worldToScreen: sx = (worldX - camPosX) * zoom + shakeX
    const halfTW = this.tileWidth / 2;
    const halfTH = this.tileHeight / 2;
    const camPosX = camera.position.x;
    const camPosY = camera.position.y;
    const shakeX = camera.shakeOffsetX;
    const shakeY = camera.shakeOffsetY;
    const margin = 100 * zoom;

    // Reusable transparent props buffer (avoids per-frame allocation)
    let tpCount = 0;

    // Pre-fetch player tile coords for occlusion check
    const ptx = playerTilePos ? playerTilePos.x : 0;
    const pty = playerTilePos ? playerTilePos.y : 0;

    for (const prop of this.sortedPropsCache) {
      // Inlined tileToWorld + worldToScreen to avoid 2 Vector2 allocations per prop
      const worldX = (prop.x - prop.y) * halfTW;
      const worldY = (prop.x + prop.y) * halfTH;
      const sx = (worldX - camPosX) * zoom + shakeX;
      const sy = (worldY - camPosY) * zoom + shakeY;

      // Skip props that are definitely off screen
      if (sx < -margin || sx > screenW + margin ||
          sy < -margin || sy > screenH + margin) {
        continue;
      }

      // Check if player is behind this prop (for transparency)
      let shouldBeTransparent = false;

      if (playerTilePos) {
        const asset = PropRegistry.get(prop.type);
        if (asset && asset.occludesPlayer) {
          const dx = ptx - prop.x;
          const dy = pty - prop.y;
          // Player is behind if they're within prop's width and their Y is greater
          if (dy > 0 && dy < 3 && (dx > -2 && dx < 2)) {
            shouldBeTransparent = true;
          }
        }
      }

      if (shouldBeTransparent) {
        // Store in pre-allocated buffer
        if (tpCount < this._tpBuf.length) {
          this._tpBuf[tpCount].type = prop.type;
          this._tpBuf[tpCount].sx = sx;
          this._tpBuf[tpCount].sy = sy;
          this._tpBuf[tpCount].variant = prop.variant;
        } else {
          this._tpBuf.push({ type: prop.type, sx, sy, variant: prop.variant });
        }
        tpCount++;
      } else {
        PropRegistry.render(ctx, prop.type, sx, sy, zoom, prop.variant);
      }
    }

    // Batch render all transparent props in a single globalAlpha block
    if (tpCount > 0) {
      ctx.save();
      ctx.globalAlpha = 0.35;
      for (let i = 0; i < tpCount; i++) {
        const tp = this._tpBuf[i];
        PropRegistry.render(ctx, tp.type, tp.sx, tp.sy, zoom, tp.variant);
      }
      ctx.restore();
    }
  }

  private renderBridgeTile(
    ctx: CanvasRenderingContext2D,
    screenX: number,
    screenY: number,
    zoom: number
  ): void {
    const cachedTile = getOrCreateBridgeTile(zoom, this.tileWidth, this.tileHeight);
    ctx.drawImage(
      cachedTile,
      screenX - cachedTile.width / 2,
      screenY - cachedTile.height / 2
    );
  }

  getWidth(): number {
    return this.width;
  }

  getHeight(): number {
    return this.height;
  }

  getTileWidth(): number {
    return this.tileWidth;
  }

  getTileHeight(): number {
    return this.tileHeight;
  }

  getZoneId(): ZoneType | null {
    return this.zoneId;
  }

  // Get center of map in world coordinates
  getCenter(): Vector2 {
    return this.tileToWorld(this.width / 2, this.height / 2);
  }

  // ============== EDITOR METHODS ==============

  // Set tile type at position
  setTile(tileX: number, tileY: number, type: TileType): boolean {
    if (!this.isValidTile(tileX, tileY)) return false;

    const tile = this.tiles[tileY][tileX];
    tile.type = type;
    // Update walkability based on tile type
    tile.walkable = type !== TileType.WATER && type !== TileType.LAVA && type !== TileType.VOID && type !== TileType.MOUNTAIN && type !== TileType.DEEP_WATER && type !== TileType.CLIFF_EDGE;
    // Update heightmap
    if (this.heightmap[tileY]) {
      this.heightmap[tileY][tileX] = this.tileTypeToElevation(type);
    }
    return true;
  }

  // Set tile walkability manually (for spawn/walkable areas)
  setTileWalkable(tileX: number, tileY: number, walkable: boolean): boolean {
    if (!this.isValidTile(tileX, tileY)) return false;
    this.tiles[tileY][tileX].walkable = walkable;
    return true;
  }

  // Add a prop to the map
  addProp(prop: PropPlacement): void {
    // Check if prop already exists at this position
    const existing = this.props.findIndex(p =>
      Math.abs(p.x - prop.x) < 0.5 && Math.abs(p.y - prop.y) < 0.5
    );
    if (existing !== -1) {
      // Replace existing prop
      this.props[existing] = prop;
    } else {
      this.props.push(prop);
    }
    this.propsDirty = true;
    this.buildPropGrid();
  }

  // Remove prop at position
  removeProp(tileX: number, tileY: number, radius: number = 1): boolean {
    const initialLength = this.props.length;
    this.props = this.props.filter(p => {
      const dx = p.x - tileX;
      const dy = p.y - tileY;
      return Math.sqrt(dx * dx + dy * dy) > radius;
    });
    this.propsDirty = true;
    this.buildPropGrid();
    return this.props.length < initialLength;
  }

  // Get prop at position (or null)
  getPropAt(tileX: number, tileY: number, radius: number = 1): PropPlacement | null {
    for (const prop of this.props) {
      const dx = prop.x - tileX;
      const dy = prop.y - tileY;
      if (Math.sqrt(dx * dx + dy * dy) <= radius) {
        return prop;
      }
    }
    return null;
  }

  // Add a spawn point
  addSpawn(spawn: SpawnPoint): void {
    // Check if spawn already exists at this position
    const existing = this.spawns.findIndex(s =>
      Math.abs(s.x - spawn.x) < 0.5 && Math.abs(s.y - spawn.y) < 0.5
    );
    if (existing !== -1) {
      // Replace existing spawn
      this.spawns[existing] = spawn;
    } else {
      this.spawns.push(spawn);
    }
  }

  // Remove spawn at position
  removeSpawn(tileX: number, tileY: number, radius: number = 1): boolean {
    const initialLength = this.spawns.length;
    this.spawns = this.spawns.filter(s => {
      const dx = s.x - tileX;
      const dy = s.y - tileY;
      return Math.sqrt(dx * dx + dy * dy) > radius;
    });
    return this.spawns.length < initialLength;
  }

  // Get spawn at position
  getSpawnAt(tileX: number, tileY: number, radius: number = 1): SpawnPoint | null {
    for (const spawn of this.spawns) {
      const dx = spawn.x - tileX;
      const dy = spawn.y - tileY;
      if (Math.sqrt(dx * dx + dy * dy) <= radius) {
        return spawn;
      }
    }
    return null;
  }

  // Get all props
  getProps(): PropPlacement[] {
    return this.props;
  }

  // Get all spawns
  getSpawns(): SpawnPoint[] {
    return this.spawns;
  }

  // Get biome
  getBiome(): BiomeType {
    return this.biome;
  }

  // Clear and resize map
  resizeMap(newWidth: number, newHeight: number): void {
    const oldTiles = this.tiles;
    const oldWidth = this.width;
    const oldHeight = this.height;

    this.width = newWidth;
    this.height = newHeight;
    this.tiles = [];

    // Create new tiles array
    for (let y = 0; y < newHeight; y++) {
      this.tiles[y] = [];
      for (let x = 0; x < newWidth; x++) {
        // Copy from old tiles if available
        if (x < oldWidth && y < oldHeight && oldTiles[y] && oldTiles[y][x]) {
          this.tiles[y][x] = { ...oldTiles[y][x] };
        } else {
          // Default new tiles to grass
          this.tiles[y][x] = {
            type: TileType.GRASS,
            walkable: true,
            elevation: 0,
            variant: Math.floor(Math.random() * 4),
          };
        }
      }
    }

    // Remove props and spawns outside new bounds
    this.props = this.props.filter(p => p.x < newWidth && p.y < newHeight);
    this.propsDirty = true;
    this.spawns = this.spawns.filter(s => s.x < newWidth && s.y < newHeight);

    // Rebuild heightmap from tiles
    this.buildHeightmapFromTiles();
    this.smoothHeightmap(2);

    // Rebuild spatial hash for prop collision
    this.buildPropGrid();
  }

  // Clear entire map to default state
  clearMap(): void {
    this.props = [];
    this.propsDirty = true;
    this.propGrid.clear();
    this.spawns = [];
    this.bridgeTiles.clear();

    this.heightmap = [];
    for (let y = 0; y < this.height; y++) {
      this.heightmap[y] = [];
      for (let x = 0; x < this.width; x++) {
        this.tiles[y][x] = {
          type: TileType.GRASS,
          walkable: true,
          elevation: 0,
          variant: Math.floor(Math.random() * 4),
        };
        this.heightmap[y][x] = 0.45;
      }
    }
  }

  // Fill rectangular area with tile type
  fillRect(x1: number, y1: number, x2: number, y2: number, type: TileType): void {
    const minX = Math.max(0, Math.min(x1, x2));
    const maxX = Math.min(this.width - 1, Math.max(x1, x2));
    const minY = Math.max(0, Math.min(y1, y2));
    const maxY = Math.min(this.height - 1, Math.max(y1, y2));

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        this.setTile(x, y, type);
      }
    }
  }

  // Export map data for saving
  exportMapData(): {
    width: number;
    height: number;
    biome: BiomeType;
    tiles: number[][];
    props: PropPlacement[];
    spawns: SpawnPoint[];
  } {
    // Convert tiles to simple number array (tile types)
    const tilesData: number[][] = [];
    for (let y = 0; y < this.height; y++) {
      tilesData[y] = [];
      for (let x = 0; x < this.width; x++) {
        tilesData[y][x] = this.tiles[y][x].type;
      }
    }

    return {
      width: this.width,
      height: this.height,
      biome: this.biome,
      tiles: tilesData,
      props: [...this.props],
      spawns: [...this.spawns],
    };
  }

  // Import map data from saved format
  importMapData(data: {
    width: number;
    height: number;
    biome: BiomeType;
    tiles: number[][];
    props: PropPlacement[];
    spawns: SpawnPoint[];
  }): void {
    this.width = data.width;
    this.height = data.height;
    this.biome = data.biome;
    this.props = [...data.props];
    this.propsDirty = true;
    this.spawns = [...data.spawns];
    this.bridgeTiles.clear();

    // Recreate tiles from number array
    this.tiles = [];
    for (let y = 0; y < this.height; y++) {
      this.tiles[y] = [];
      for (let x = 0; x < this.width; x++) {
        const type = data.tiles[y]?.[x] ?? TileType.GRASS;
        this.tiles[y][x] = {
          type,
          walkable: type !== TileType.WATER && type !== TileType.LAVA && type !== TileType.VOID && type !== TileType.MOUNTAIN && type !== TileType.DEEP_WATER && type !== TileType.CLIFF_EDGE,
          elevation: 0,
          variant: Math.floor(Math.random() * 4),
        };
      }
    }

    // Build heightmap from imported tiles
    this.buildHeightmapFromTiles();
    this.smoothHeightmap(2);

    // Build spatial hash for fast prop collision
    this.buildPropGrid();
  }

  // Get all tiles as 2D array of types
  getTilesData(): TileType[][] {
    const result: TileType[][] = [];
    for (let y = 0; y < this.height; y++) {
      result[y] = [];
      for (let x = 0; x < this.width; x++) {
        result[y][x] = this.tiles[y][x].type;
      }
    }
    return result;
  }
}
